import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Brain, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;
const ENDPOINT = `${API_URL}/settings/adaptive-learning`;
// S5-032: a failed status read is retried quietly at most this many times (then the card stays
// hidden until the next visit to Settings). Covers a transient error or the minutes in which a new
// frontend may already be live in front of a backend that does not serve the endpoint yet.
export const RETRY_DELAYS_MS = [10000, 60000];

/**
 * S5-028: the company's Adaptive Learning switch. One company-wide preference, changed by an
 * active admin only; everyone else sees it read-only. The server decides everything shown here:
 * the stored preference, whether the rollout is open for this workspace, and the effective state.
 * The switch moves only after the server stored the change.
 *
 * S5-032: the card exists only for a company the server says the feature is available to
 * (`rollout_available === true`). While the status is loading, after a failed read (including
 * the 404 of a backend without the endpoint) and whenever the rollout is not available to this
 * company, nothing is rendered at all: no placeholder, no error panel, no disabled switch.
 * Availability is not maturity: an available company with the preference OFF sees the card so its
 * admin can opt in.
 */
const AdaptiveLearningCard = () => {
  const { token, isAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const attempts = useRef(0);
  const retryTimer = useRef(null);
  const loadRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(ENDPOINT, { headers: { Authorization: `Bearer ${token}` } });
      attempts.current = 0;
      setStatus(response.data);
    } catch (error) {
      setStatus(null);
      const delay = RETRY_DELAYS_MS[attempts.current];
      if (delay !== undefined) {
        attempts.current += 1;
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null;
          if (loadRef.current) loadRef.current();
        }, delay);
      }
    }
  }, [token]);

  useEffect(() => {
    loadRef.current = load;
    attempts.current = 0;
    load();
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = null;
      loadRef.current = null;
    };
  }, [load]);

  const change = async (next) => {
    if (saving) return;
    setSaving(true);
    try {
      const response = await axios.put(ENDPOINT, { enabled: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
      toast.success(next ? 'Adaptive Learning turned on' : 'Adaptive Learning turned off');
    } catch (error) {
      const code = error.response?.status;
      if (code === 409) {
        toast.error('Adaptive Learning is not available for this workspace yet.');
        await load();
      } else if (code === 403) {
        toast.error('Only an administrator of this workspace can change this setting.');
      } else {
        toast.error('Could not save the setting. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!status || status.rollout_available !== true) {
    return null;
  }

  const preference = status.preference_enabled === true;
  const effective = status.effective_enabled === true;
  const canToggle = isAdmin && !saving;

  const badge = effective ? (
    <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/40" data-testid="adaptive-learning-badge">
      On
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-white/5 text-slate-300 border-white/20" data-testid="adaptive-learning-badge">
      Off
    </Badge>
  );
  const message = effective
    ? 'Adaptive ranking is allowed for future eligible searches. Results change only when enough feedback exists.'
    : 'Standard ranking. Decisions are still collected.';

  return (
    <Card className="world-card" data-testid="adaptive-learning-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
            <Brain className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <CardTitle className="text-xl text-white">Adaptive Learning</CardTitle>
            <CardDescription className="text-slate-400">
              Use your team&apos;s feedback to improve the ranking of future searches.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4" data-testid={`adaptive-learning-${status.state || 'unknown'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Switch
              checked={preference}
              onCheckedChange={change}
              disabled={!canToggle}
              aria-label="Adaptive Learning"
              data-testid="adaptive-learning-switch"
            />
            {badge}
            {saving && <Loader2 className="h-4 w-4 animate-spin text-blue-400" data-testid="adaptive-learning-saving" />}
          </div>
          <p className="text-sm text-slate-400" data-testid="adaptive-learning-message">{message}</p>
          {!isAdmin && (
            <p className="text-sm text-slate-400" data-testid="adaptive-learning-readonly">
              Only an administrator of this workspace can change this setting.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdaptiveLearningCard;
